import { QueryClient } from "@tanstack/react-query";
import axios from "axios";

import { getAccessTokenCookie } from "./cookies";
import { getEnv } from "./env";
import { authDebug, authDebugError } from "./safeAuthLog";
import { logoutUser, refreshTokenIfNeeded } from "./tokenManager";

// Create an Axios instance with default configuration
export const axiosInstance = axios.create({
  baseURL: getEnv("VITE_BACKEND_URL") || "http://localhost:8000",
});

// Add a request interceptor to include the access_token cookie as bearer token
axiosInstance.interceptors.request.use(
  (config) => {
    // Get the access token from in-memory store (not from cookie; avoids XSS)
    const accessToken = getAccessTokenCookie();

    // If there's a token, add it to the Authorization header
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle auth errors
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 403 USER_BLOCKED — logout immediately, leave other 403s alone
    if (
      error.response?.status === 403 &&
      error.response?.data?.detail === "USER_BLOCKED"
    ) {
      await logoutUser(true);
      return Promise.reject(
        new Error("Your account has been blocked. Please contact support.")
      );
    }

    // Handle 401 errors specially
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      authDebug("axios: received 401, attempting token refresh");

      // Mark this request as retried to prevent infinite loops
      originalRequest._retry = true;

      try {
        // Try to refresh the token
        const refreshSuccess = await refreshTokenIfNeeded();

        if (refreshSuccess) {
          authDebug("axios: token refresh successful, retrying request");
          // Get the new token from in-memory store
          const accessToken = getAccessTokenCookie();

          if (accessToken) {
            // Update the authorization header with the new token
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;

            // Retry the original request
            return axiosInstance(originalRequest);
          }
        } else {
          authDebug("axios: token refresh failed; logging out");
          // Clear authentication state and redirect to login
          await logoutUser(true);
          return Promise.reject(
            new Error("Authentication failed. Please sign in again.")
          );
        }
      } catch (refreshError) {
        authDebugError("axios: token refresh error", refreshError);
        // Clear authentication state and redirect to login
        await logoutUser(true);
        return Promise.reject(
          new Error("Authentication failed. Please sign in again.")
        );
      }
    }

    return Promise.reject(error);
  }
);

// Create the React Query client
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

export default queryClient;
